import useSWR from "swr";

import { IdentityProvider, isScimEnabled, OIDCIdpWithoutId, SAMLIdpUpdateFields } from "../../domain/IdentityProvider";
import { useServicesContext } from "../../providers/ServicesProvider";
import { useEffect, useMemo } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { getErrorMessage } from "../../lib/utils";

export const useOrganizationIdp = (organizationId?: string) => {
    const { idpService, organizationService } = useServicesContext();

    // todo: remove IdentityProvider type, switch to using the OIDC service SAML method
    const {
        mutate: mutateIdp,
        data: identityProviders,
        error
    } = useSWR<IdentityProvider[] | null, Error>(
        organizationId ? `/api/organizations/${organizationId}/identityproviders` : null,
        () => {
            if (!organizationId) {
                return null;
            }

            return idpService!.getOrganizationIdentityProviders({ organizationId });
        }
    );

    useEffect(() => {
        if (error) {
            datadogRum.addError(error);
        }
    }, [error]);

    // Derived IdP groupings for the Domains / Dynamic Provisioning page. The SCIM-exclusion
    // rule (SCIM-managed IdPs own provisioning, so they can't do domain-based dynamic
    // provisioning) lives here rather than in the page so it has a single home and any caller
    // of this hook gets the same partitioning.
    const idpsWithDomains = useMemo(
        () => (identityProviders || []).filter((idp) => !!idp.domains?.length),
        [identityProviders]
    );
    const idpsEligibleForDomainAdd = useMemo(
        () => (identityProviders || []).filter((idp) => !idp.domains?.length && !isScimEnabled(idp)),
        [identityProviders]
    );
    const scimManagedIdpsWithoutDomains = useMemo(
        () => (identityProviders || []).filter((idp) => !idp.domains?.length && isScimEnabled(idp)),
        [identityProviders]
    );

    const addSAMLProvider = async ({
        issuer,
        ssoUrl,
        certificate,
        idpName
    }: {
        issuer: string;
        ssoUrl: string;
        certificate: File;
        idpName: string;
    }): Promise<IdentityProvider> => {
        const idp = await idpService?.addSAMLProvider({
            organizationId: organizationId as string,
            issuer,
            ssoUrl,
            certificate,
            idpName
        });

        await mutateIdp([...(identityProviders || []), idp as IdentityProvider]);

        return idp as IdentityProvider;
    };

    const uploadSAMLIdentityProviderCertificate = async ({
        identityProviderId,
        certificate
    }: {
        identityProviderId: string;
        certificate: File;
    }): Promise<IdentityProvider> => {
        const { expiresAt } = await idpService!.uploadSAMLIdentityProviderCertificate({
            organizationId: organizationId as string,
            identityProviderId,
            certificate
        });
        const updatedIdp = {
            ...identityProviders?.find((idp) => idp.id === identityProviderId),
            expiresAt: expiresAt || undefined
        } as IdentityProvider;
        await mutateIdp([
            ...(identityProviders || []).map((idp) => (idp.id === identityProviderId ? updatedIdp : idp))
        ]);
        return updatedIdp;
    };

    const addOIDCProvider = async (oidcProvider: OIDCIdpWithoutId): Promise<IdentityProvider> => {
        const result = await idpService?.addOIDCProvider({
            ...oidcProvider,
            organizationId: organizationId as string
        });
        const idp = result as IdentityProvider;

        await mutateIdp([...(identityProviders || []), idp]);

        return idp;
    };

    const updateSAMLProvider = async (samlProviderDetails: SAMLIdpUpdateFields): Promise<IdentityProvider> => {
        const updatedIdp = await idpService!.updateSAMLIdentityProvider({
            ...samlProviderDetails,
            organizationId: organizationId as string,
            issuerUrl: samlProviderDetails.issuerUrl ?? "",
            ssoUrl: samlProviderDetails.ssoUrl ?? ""
        });
        await mutateIdp([...(identityProviders || []).map((idp) => (idp.id === updatedIdp.id ? updatedIdp : idp))]);
        return updatedIdp;
    };

    const toggleMFA = async ({ enabled }: { enabled: boolean }) => {
        return await organizationService!.updateOrganization({
            id: organizationId as string,
            mfaEnabled: enabled
        });
    };

    const deleteOrganizationProviders = async () => {
        await idpService!.deleteOrganizationIdentityProviders({
            organizationId: organizationId as string
        });
        await mutateIdp(null);

        return null;
    };

    const deleteOrganizationProvider = async ({ id }: { id: string }) => {
        await idpService!.deleteOrganizationIdentityProvider({
            organizationId: organizationId as string,
            id: id
        });
        await mutateIdp(null);

        return null;
    };

    const updateIdpDomains = async ({
        domains,
        identityProviderId
    }: {
        domains: string[];
        identityProviderId: string;
    }) => {
        // Defense-in-depth: SCIM-managed IdPs own provisioning, so their domains must never be
        // written from here. The UI also disables this path, but enforce it at the data layer
        // so a future/programmatic caller can't bypass the rule. If the ID isn't in the cached
        // list we can't determine SCIM status client-side, so we intentionally fall through and
        // rely on the backend as the authoritative gate rather than block a possibly-valid update.
        const targetIdp = (identityProviders || []).find((idp) => idp.id === identityProviderId);
        if (targetIdp && isScimEnabled(targetIdp)) {
            throw new Error("Domains cannot be updated for a SCIM-managed identity provider.");
        }
        const updatedDomains = await idpService!.updateIdentityProviderDomains({
            identityProviderId,
            organizationId: organizationId as string,
            domains
        });
        const updatedIdps = [...(identityProviders || [])];
        const updateIdx = updatedIdps.findIndex((idp) => idp.id === identityProviderId);
        updatedIdps[updateIdx] = updatedDomains;

        await mutateIdp(updatedIdps);

        return updatedDomains;
    };

    const enableScimForIdentityProvider = async ({ identityProviderId }: { identityProviderId: string }) => {
        const updatedIdp = await idpService!.enableScimForIdentityProvider({
            organizationId: organizationId as string,
            identityProviderId
        });
        // Update the local cache with the updated IdP
        const updatedIdps = [...(identityProviders || [])];
        const updateIdx = updatedIdps.findIndex((idp) => idp.id === identityProviderId);
        if (updateIdx !== -1) {
            updatedIdps[updateIdx] = updatedIdp;
            await mutateIdp(updatedIdps);
        }
        return updatedIdp;
    };

    const removeSCIMForIdentityProvider = async ({ identityProviderId }: { identityProviderId: string }) => {
        try {
            await idpService!.removeSCIMForIdentityProvider({
                organizationId: organizationId as string,
                identityProviderId
            });
            // Update the local cache with the updated IdP
            const updatedIdps = [...(identityProviders || [])];
            const updateIdx = updatedIdps.findIndex((idp) => idp.id === identityProviderId);
            if (updateIdx !== -1) {
                updatedIdps[updateIdx].scimApiKey = undefined;
                await mutateIdp(updatedIdps);
            }
            return { success: true };
        } catch (e) {
            return { success: false, error: getErrorMessage(e) };
        }
    };

    return {
        addOIDCProvider,
        addSAMLProvider,
        deleteOrganizationProvider,
        deleteOrganizationProviders,
        enableScimForIdentityProvider,
        error,
        identityProviders: identityProviders || null,
        idpsWithDomains,
        idpsEligibleForDomainAdd,
        scimManagedIdpsWithoutDomains,
        isLoadingIdp: !error && identityProviders === undefined,
        mutateIdp,
        removeSCIMForIdentityProvider,
        toggleMFA,
        updateIdpDomains,
        updateSAMLProvider,
        uploadSAMLIdentityProviderCertificate
    };
};
